import { useCallback, useEffect, useMemo } from "react";
import {
	GestureStateChangeEvent,
	GestureUpdateEvent,
	PanGestureHandlerEventPayload,
	PinchGestureChangeEventPayload,
	PinchGestureHandlerEventPayload,
	TapGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import {
	cancelAnimation,
	runOnJS,
	useDerivedValue,
	useSharedValue,
	withDecay,
	withTiming,
} from "react-native-reanimated";
import haptic from "src/helpers/haptic";
import usePhotoDetailStore, { PhotoDetailStore } from "../stores/usePhotoDetailStore";
import useImageGalleryDimensions from "./useImageGalleryDimensions";

const minZoomScale = 1;
const maxZoomScale = 5;

const bound = (value: number, min: number, max: number) => {
	"worklet";
	return Math.max(Math.min(value, max), min);
};

/**
 * If the value is less than min, then it returns the value + delta only if delta brings it closer to min
 * If the value is greater than max, then it returns the value + delta only if delta brings it closer to max
 */
const deltaBound = (value: number, delta: number, min: number, max: number) => {
	"worklet";
	if (value + delta < min) {
		return delta > 0 ? value + delta : value;
	}
	if (value + delta > max) {
		return delta < 0 ? value + delta : value;
	}
	return value + delta;
};

const squareEasing = (x: number) => {
	"worklet";
	return x * x;
};

const useZoomGestureHandlers = () => {
	const zoomScale = useSharedValue(1);
	const pinchXOffset = useSharedValue(0);
	const pinchYOffset = useSharedValue(0);
	const panXOffset = useSharedValue(0);
	const panYOffset = useSharedValue(0);

	const isVideoSV = useSharedValue(false);
	useEffect(() => {
		usePhotoDetailStore.subscribe(
			(state) => state.debouncedSettledPhoto?.variant === "Video",
			(isVideo) => {
				isVideoSV.value = isVideo;
			},
		);
		isVideoSV.value = usePhotoDetailStore.getState().debouncedSettledPhoto?.variant === "Video";
	}, [isVideoSV]);

	const zoomXOffset = useDerivedValue(
		() => pinchXOffset.value + panXOffset.value,
		[pinchXOffset, panXOffset],
	);
	const zoomYOffset = useDerivedValue(
		() => pinchYOffset.value + panYOffset.value,
		[pinchYOffset, panYOffset],
	);

	const { mainItemDimensions } = useImageGalleryDimensions();
	const { offsetBounds } = useMemo(() => {
		const bounds = {
			x0: 0,
			y0: 0,
			x1: mainItemDimensions.width,
			y1: mainItemDimensions.height,
		};
		const boundsAllowance = 1 / 8;
		// These are super convoluted and backwards. The coordinate system is inverted in the x-axis
		const offsetBounds = {
			// x0 affects right side boundary
			x0: bounds.x0 - mainItemDimensions.width * boundsAllowance,
			// y0 affects bottom side boundary
			y0: bounds.y0 - mainItemDimensions.height * boundsAllowance,
			x1: bounds.x1 - mainItemDimensions.width * (1 - boundsAllowance),
			y1: bounds.y1 - mainItemDimensions.height * (1 - boundsAllowance),
		};
		return { bounds, offsetBounds };
	}, [mainItemDimensions]);

	const { xOffset, yOffset } = useMemo(
		() => ({
			xOffset: -mainItemDimensions.width / 2,
			yOffset: -mainItemDimensions.height / 2,
		}),
		[mainItemDimensions.height, mainItemDimensions.width],
	);

	const initialFocalX = useSharedValue(0);
	const initialFocalY = useSharedValue(0);
	const pinchTargetXOffset = useSharedValue(0);
	const pinchTargetYOffset = useSharedValue(0);
	const setMode = useCallback((newMode: PhotoDetailStore["mode"]) => {
		const { mode, setMode } = usePhotoDetailStore.getState();
		if (mode === newMode) return;

		if (mode === "Zoom") {
			haptic("Medium");
		}
		if (mode === "Regular") {
			haptic("Light");
		}

		setMode(newMode);
	}, []);
	const handleZoomPinchStart = useCallback(
		(event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>) => {
			"worklet";
			if (isVideoSV.value) {
				runOnJS(setMode)("FullScreen");
				return;
			}
			runOnJS(setMode)("Zoom");

			cancelAnimation(panXOffset);
			cancelAnimation(panYOffset);

			initialFocalX.value = event.focalX;
			initialFocalY.value = event.focalY;

			// These are the offsets from the focal point to the real center
			const offsetFocalX = (-xOffset - event.focalX) / zoomScale.value;
			const offsetFocalY = (-yOffset - event.focalY) / zoomScale.value;

			const newTargetXOffset = pinchXOffset.value + offsetFocalX;
			const newTargetYOffset = pinchYOffset.value + offsetFocalY;

			pinchTargetXOffset.value = newTargetXOffset;
			pinchTargetYOffset.value = newTargetYOffset;
		},
		[
			setMode,
			panXOffset,
			panYOffset,
			initialFocalX,
			initialFocalY,
			xOffset,
			zoomScale,
			yOffset,
			pinchXOffset,
			pinchYOffset,
			pinchTargetXOffset,
			pinchTargetYOffset,
			isVideoSV,
		],
	);

	const handleZoomPinchChange = useCallback(
		(
			event: GestureUpdateEvent<
				PinchGestureHandlerEventPayload & PinchGestureChangeEventPayload
			>,
		) => {
			"worklet";
			if (isVideoSV.value) return;
			// Negative when zooming out
			const deltaScale = event.scaleChange - 1;
			const newZoomScale = bound(
				zoomScale.value + (deltaScale < 0 ? 3 * deltaScale : 2.5 * deltaScale),
				minZoomScale,
				maxZoomScale,
			);
			zoomScale.value = newZoomScale;

			if (deltaScale < 0) {
				// Want to bring everything back to 0, 0
				const deltaFactor = Math.abs((2 * deltaScale) / newZoomScale);
				panXOffset.value += deltaFactor * (0 - panXOffset.value);
				panYOffset.value += deltaFactor * (0 - panYOffset.value);
				pinchXOffset.value += deltaFactor * (0 - pinchXOffset.value);
				pinchYOffset.value += deltaFactor * (0 - pinchYOffset.value);
			} else {
				const deltaFactor = Math.abs((4 * deltaScale) / newZoomScale);
				pinchXOffset.value += deltaFactor * (pinchTargetXOffset.value - pinchXOffset.value);
				pinchYOffset.value += deltaFactor * (pinchTargetYOffset.value - pinchYOffset.value);
			}
		},
		[
			pinchXOffset,
			pinchYOffset,
			pinchTargetXOffset,
			pinchTargetYOffset,
			zoomScale,
			panXOffset,
			panYOffset,
			isVideoSV,
		],
	);

	const handleZoomPinchEnd = useCallback(
		(event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>) => {
			"worklet";
			if (zoomScale.value <= 1) {
				runOnJS(setMode)("Regular");
			}
		},
		[zoomScale, setMode],
	);

	const panPrevX = useSharedValue(0);
	const panPrevY = useSharedValue(0);
	const handleZoomPanStart = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			if (isVideoSV.value) {
				runOnJS(setMode)("FullScreen");
				return;
			}
			panPrevX.value = event.absoluteX;
			panPrevY.value = event.absoluteY;

			cancelAnimation(panXOffset);
			cancelAnimation(panYOffset);
		},
		[panPrevX, panPrevY, panXOffset, panYOffset, isVideoSV, setMode],
	);

	const handleZoomPanChange = useCallback(
		(event: GestureUpdateEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			if (isVideoSV.value) return;
			const deltaX = event.absoluteX - panPrevX.value;
			const deltaY = event.absoluteY - panPrevY.value;

			panXOffset.value = deltaBound(
				panXOffset.value,
				deltaX / zoomScale.value,
				zoomScale.value * offsetBounds.x0 - pinchXOffset.value,
				zoomScale.value * offsetBounds.x1 - pinchXOffset.value,
			);
			panYOffset.value = deltaBound(
				panYOffset.value,
				deltaY / zoomScale.value,
				zoomScale.value * offsetBounds.y0 - pinchYOffset.value,
				zoomScale.value * offsetBounds.y1 - pinchYOffset.value,
			);

			panPrevX.value = event.absoluteX;
			panPrevY.value = event.absoluteY;
		},
		[
			offsetBounds,
			panPrevX,
			panPrevY,
			panXOffset,
			panYOffset,
			zoomScale,
			pinchXOffset,
			pinchYOffset,
			isVideoSV,
		],
	);

	const handleZoomPanEnd = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			if (isVideoSV.value) return;
			panXOffset.value = withDecay({
				velocity: event.velocityX / zoomScale.value,
				deceleration: 0.997,
				clamp: [
					// We don't want to clamp tighter than we are currently, otherwise it teleports
					Math.min(
						zoomScale.value * offsetBounds.x0 - pinchXOffset.value,
						panXOffset.value,
					),
					Math.max(
						zoomScale.value * offsetBounds.x1 - pinchXOffset.value,
						panXOffset.value,
					),
				],
			});
			panYOffset.value = withDecay({
				velocity: event.velocityY / zoomScale.value,
				deceleration: 0.997,
				clamp: [
					Math.min(
						zoomScale.value * offsetBounds.y0 - pinchYOffset.value,
						panYOffset.value,
					),
					Math.max(
						zoomScale.value * offsetBounds.y1 - pinchYOffset.value,
						panYOffset.value,
					),
				],
			});
		},
		[panXOffset, panYOffset, zoomScale, offsetBounds, pinchXOffset, pinchYOffset, isVideoSV],
	);

	const mode = usePhotoDetailStore((state) => state.mode);
	const modeSV = useSharedValue<PhotoDetailStore["mode"]>(mode);
	useEffect(() => {
		modeSV.value = mode;
	}, [mode, modeSV]);
	const handleDoubleTapEnd = useCallback(
		(event: GestureStateChangeEvent<TapGestureHandlerEventPayload>) => {
			"worklet";
			if (isVideoSV.value) {
				runOnJS(setMode)("FullScreen");
				return;
			}
			if (modeSV.value === "Zoom") {
				runOnJS(setMode)("Regular");
				modeSV.value = "Regular";
			} else {
				runOnJS(setMode)("Zoom");
				modeSV.value = "Zoom";

				zoomScale.value = withTiming(2, {
					easing: squareEasing,
				});
				pinchXOffset.value = withTiming(-(event.x - mainItemDimensions.width / 2));
				pinchYOffset.value = withTiming(-(event.y - mainItemDimensions.height / 2));
			}
		},
		[
			modeSV,
			zoomScale,
			setMode,
			pinchXOffset,
			mainItemDimensions.width,
			mainItemDimensions.height,
			pinchYOffset,
			isVideoSV,
		],
	);

	useEffect(() => {
		usePhotoDetailStore.getState().setZoomHooks({ zoomScale, zoomXOffset, zoomYOffset });
		return () => {
			usePhotoDetailStore.getState().clearZoomHooks();
		};
	}, [zoomScale, zoomXOffset, zoomYOffset]);

	useEffect(() => {
		// Logic to handle zooming is handled by whoever called it
		if (mode !== "Zoom") {
			pinchXOffset.value = withTiming(0);
			pinchYOffset.value = withTiming(0);
			panXOffset.value = withTiming(0);
			panYOffset.value = withTiming(0);
			pinchTargetXOffset.value = 0;
			pinchTargetYOffset.value = 0;
			zoomScale.value = withTiming(1);
		}
	}, [
		mode,
		panXOffset,
		panYOffset,
		pinchTargetXOffset,
		pinchTargetYOffset,
		pinchXOffset,
		pinchYOffset,
		zoomScale,
	]);

	return {
		handleZoomPinchStart,
		handleZoomPinchChange,
		handleZoomPinchEnd,
		handleZoomPanStart,
		handleZoomPanChange,
		handleZoomPanEnd,
		handleDoubleTapEnd,
	};
};
export default useZoomGestureHandlers;
